"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const PreviewKeyGate_1 = require("./PreviewKeyGate");
const fmt = (sec) => `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;
function pickMimeType() {
    if (typeof MediaRecorder === 'undefined')
        return undefined;
    const options = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac'];
    return options.find((t) => MediaRecorder.isTypeSupported?.(t));
}
function WalkthroughRecorder({ apiBaseUrl, businessId, pathname, navigate, pageNameFor, steps }) {
    const [key, setKey] = (0, react_1.useState)(null);
    const [phase, setPhase] = (0, react_1.useState)('idle');
    const [mode, setMode] = (0, react_1.useState)('explore');
    const [stepIndex, setStepIndex] = (0, react_1.useState)(0);
    const [elapsed, setElapsed] = (0, react_1.useState)(0);
    const [message, setMessage] = (0, react_1.useState)('');
    const recorderRef = (0, react_1.useRef)(null);
    const chunksRef = (0, react_1.useRef)([]);
    const startedAtRef = (0, react_1.useRef)(0);
    const timelineRef = (0, react_1.useRef)([]);
    const timerRef = (0, react_1.useRef)(null);
    const pendingRef = (0, react_1.useRef)(null);
    // Re-check the access code on every page - it's entered on the Presentation/Dashboard tab.
    (0, react_1.useEffect)(() => {
        setKey(businessId ? (0, PreviewKeyGate_1.readStoredPreviewKey)(businessId) : null);
    }, [businessId, pathname]);
    // Note each page they reach while recording.
    (0, react_1.useEffect)(() => {
        if (phase !== 'recording')
            return;
        const page = pageNameFor(pathname);
        const last = timelineRef.current[timelineRef.current.length - 1];
        if (!last || last.page !== page) {
            timelineRef.current.push({ at: (Date.now() - startedAtRef.current) / 1000, page });
        }
    }, [pathname, phase, pageNameFor]);
    // Leaving the site (refresh, closing the tab, typing an address) would lose the recording.
    (0, react_1.useEffect)(() => {
        if (phase !== 'recording')
            return;
        const warn = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [phase]);
    (0, react_1.useEffect)(() => () => {
        if (timerRef.current)
            clearInterval(timerRef.current);
        recorderRef.current?.stream.getTracks().forEach((t) => t.stop());
    }, []);
    if (!businessId || !key)
        return null;
    const start = async (m) => {
        setMode(m);
        setMessage('');
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = pickMimeType();
            const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            chunksRef.current = [];
            rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
            rec.start(1000);
            recorderRef.current = rec;
            startedAtRef.current = Date.now();
            timelineRef.current = [];
            setElapsed(0);
            timerRef.current = setInterval(() => setElapsed((Date.now() - startedAtRef.current) / 1000), 500);
            setPhase('recording');
            if (m === 'tour' && steps.length > 0) {
                setStepIndex(0);
                if (pathname !== steps[0].path)
                    navigate(steps[0].path);
            }
        }
        catch {
            setPhase('error');
            setMessage("We couldn't use your microphone. Check your browser allowed it, then try again.");
        }
    };
    /** Stops the microphone and packages the recording + page timeline, ready to send. */
    const stopAndSend = async () => {
        const rec = recorderRef.current;
        if (!rec)
            return;
        if (timerRef.current)
            clearInterval(timerRef.current);
        setPhase('sending');
        if (rec.state !== 'inactive') {
            const stopped = new Promise((resolve) => {
                rec.onstop = () => resolve();
            });
            rec.stop();
            await stopped;
        }
        rec.stream.getTracks().forEach((t) => t.stop());
        recorderRef.current = null;
        const type = rec.mimeType || 'audio/webm';
        const ext = type.includes('mp4') || type.includes('aac') ? 'm4a' : 'webm';
        const pages = timelineRef.current.map((t) => t.page);
        pendingRef.current = {
            file: new File([new Blob(chunksRef.current, { type })], `walkthrough-${Date.now()}.${ext}`, { type }),
            title: `🎙 ${mode === 'tour' ? 'Guided tour' : 'Walkthrough'}: ${Array.from(new Set(pages)).join(' → ') || pageNameFor(pathname)}`.slice(0, 300),
            description: `Pages visited while recording:\n${timelineRef.current.map((t) => `${fmt(t.at)}  ${t.page}`).join('\n')}`,
        };
        await send();
    };
    /** Sends the packaged recording. Kept on failure, so "Try again" resends the same thing. */
    const send = async () => {
        const pending = pendingRef.current;
        if (!pending || !key)
            return;
        setPhase('sending');
        const form = new FormData();
        form.append('media', pending.file);
        form.append('title', pending.title);
        form.append('description', pending.description);
        try {
            const res = await fetch(`${apiBaseUrl}/crm/presentation/${businessId}/activities/media`, {
                method: 'POST',
                headers: { 'X-Presentation-Key': key },
                body: form,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok)
                throw new Error(data.error || 'Upload failed');
            pendingRef.current = null;
            // Transcribe in the background - they don't need to wait for it.
            void fetch(`${apiBaseUrl}/crm/presentation/${businessId}/activities/${data.activity.id}/transcribe`, {
                method: 'POST',
                headers: { 'X-Presentation-Key': key },
            }).catch(() => undefined);
            setPhase('sent');
            setMessage('Thank you - that’s with us now.');
            setTimeout(() => setPhase((p) => (p === 'sent' ? 'idle' : p)), 5000);
        }
        catch {
            setPhase('error');
            setMessage('That didn’t send - check your connection and try again. Your recording is still here.');
        }
    };
    const bar = {
        position: 'fixed', left: 12, bottom: 12, zIndex: 999, width: 'min(360px, calc(100vw - 24px))', background: 'white', borderRadius: 16,
        boxShadow: '0 10px 32px rgba(0,0,0,0.22)', padding: 14, fontFamily: 'inherit', color: '#222',
    };
    const btn = (bg, fg = 'white') => ({
        padding: '10px 14px', borderRadius: 10, border: 'none', background: bg, color: fg, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
    });
    if (phase === 'idle') {
        return ((0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => setPhase('choosing'), style: { position: 'fixed', left: 16, bottom: 16, zIndex: 999, ...btn('#1c2b2e'), borderRadius: 999, padding: '12px 16px', boxShadow: '0 6px 20px rgba(0,0,0,0.3)' }, children: "\uD83C\uDF99 Talk us through it" }));
    }
    if (phase === 'choosing') {
        return ((0, jsx_runtime_1.jsxs)("div", { style: bar, children: [(0, jsx_runtime_1.jsx)("div", { style: { fontWeight: 700, fontSize: 15, marginBottom: 6 }, children: "\uD83C\uDF99 Talk us through your site" }), (0, jsx_runtime_1.jsx)("p", { style: { fontSize: 13, color: '#555', margin: '0 0 12px', lineHeight: 1.5 }, children: "We record your voice while you click around - just say what you like and what you\u2019d change. It keeps recording as you move between pages, and we\u2019ll know which page you were on." }), (0, jsx_runtime_1.jsxs)("div", { style: { display: 'flex', flexDirection: 'column', gap: 8 }, children: [steps.length > 0 && ((0, jsx_runtime_1.jsxs)("button", { type: "button", onClick: () => start('tour'), style: btn('#667eea'), children: ["\uD83E\uDDED Take the guided tour (", steps.length, " stops)"] })), (0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => start('explore'), style: btn('#065f46'), children: "\uD83D\uDD0D Explore on my own" }), (0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => setPhase('idle'), style: { ...btn('white', '#888'), padding: 6 }, children: "Not now" })] })] }));
    }
    if (phase === 'sending' || phase === 'sent' || phase === 'error') {
        return ((0, jsx_runtime_1.jsxs)("div", { style: bar, children: [(0, jsx_runtime_1.jsx)("div", { style: { fontSize: 14, fontWeight: 700, color: phase === 'error' ? '#b91c1c' : '#065f46' }, children: phase === 'sending' ? 'Sending your recording…' : phase === 'sent' ? '✓ Sent' : '⚠ Something went wrong' }), message && (0, jsx_runtime_1.jsx)("div", { style: { fontSize: 13, color: '#555', marginTop: 4 }, children: message }), phase === 'error' && ((0, jsx_runtime_1.jsxs)("div", { style: { display: 'flex', gap: 8, marginTop: 10 }, children: [pendingRef.current && (0, jsx_runtime_1.jsx)("button", { type: "button", onClick: send, style: btn('#667eea'), children: "Try sending again" }), (0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => {
                                if (pendingRef.current && !window.confirm('Discard this recording?'))
                                    return;
                                pendingRef.current = null;
                                setPhase('idle');
                            }, style: btn('#eee', '#333'), children: "Close" })] }))] }));
    }
    // Recording.
    const step = mode === 'tour' ? steps[stepIndex] : null;
    const isLast = mode === 'tour' && stepIndex >= steps.length - 1;
    const page = pageNameFor(pathname);
    return ((0, jsx_runtime_1.jsxs)("div", { style: bar, children: [(0, jsx_runtime_1.jsxs)("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }, children: [(0, jsx_runtime_1.jsxs)("span", { style: { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14 }, children: [(0, jsx_runtime_1.jsx)("span", { style: { width: 10, height: 10, borderRadius: '50%', background: '#dc2626', display: 'inline-block', animation: 'mango-rec-pulse 1.2s infinite' } }), "Recording ", fmt(elapsed)] }), (0, jsx_runtime_1.jsx)("span", { style: { fontSize: 12, color: '#888' }, children: mode === 'tour' ? `Stop ${stepIndex + 1} of ${steps.length}` : `On: ${page}` })] }), (0, jsx_runtime_1.jsx)("style", { children: '@keyframes mango-rec-pulse{0%,100%{opacity:1}50%{opacity:.3}}' }), step ? ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("div", { style: { fontWeight: 700, fontSize: 14 }, children: step.title }), (0, jsx_runtime_1.jsx)("div", { style: { fontSize: 13, color: '#444', lineHeight: 1.5, margin: '4px 0 10px' }, children: step.prompt }), pathname !== step.path && ((0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => navigate(step.path), style: { ...btn('white', '#4338ca'), padding: 0, fontSize: 12, marginBottom: 8 }, children: "\u21A9 Back to this stop" }))] })) : ((0, jsx_runtime_1.jsxs)("div", { style: { fontSize: 13, color: '#444', lineHeight: 1.5, marginBottom: 10 }, children: ["Tell us what works on the ", (0, jsx_runtime_1.jsx)("strong", { children: page }), " page, what you\u2019d change, and what\u2019s missing. Click anywhere - we\u2019ll keep recording."] })), (0, jsx_runtime_1.jsxs)("div", { style: { display: 'flex', gap: 8 }, children: [step && !isLast && ((0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => {
                            const next = stepIndex + 1;
                            setStepIndex(next);
                            navigate(steps[next].path);
                        }, style: { ...btn('#667eea'), flex: 1 }, children: "Next stop \u2192" })), (0, jsx_runtime_1.jsxs)("button", { type: "button", onClick: stopAndSend, style: { ...btn('#dc2626'), flex: 1 }, children: ["\u25A0 ", isLast || mode === 'explore' ? 'Stop & send' : 'Finish early'] })] }), (0, jsx_runtime_1.jsx)("div", { style: { fontSize: 11, color: '#999', marginTop: 8 }, children: "Keep this tab open - refreshing will end the recording." })] }));
}
exports.default = WalkthroughRecorder;
